import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useWeightTestViewModel } from "../../../viewmodels/operation/weighttest/useWeightTestViewModel";

import ReportHeader from "../../../components/reports/ReportHeader";

import ReportFooter from "../../../components/reports/ReportFooter";

const ViewWeightTest = () => {
  const { id } = useParams();

  const { getWeightTestById } = useWeightTestViewModel();

  const [weightTest, setWeightTest] = useState<any>(null);

  // ================= DATE FORMAT =================
  const formatDate = (date: string) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-GB");
  };

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getWeightTestById(id);

    if (data) {
      setWeightTest(data);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= PRINT =================
  const handlePrint = () => {
    window.print();
  };

  if (!weightTest) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* PRINT STYLE */}
      <style>
        {`
          @page {
            size: A4 portrait;
            margin: 4mm;
          }

          @media print {
            html,
            body {
              width: 210mm;
              height: 297mm;
              background: white !important;
              overflow: hidden !important;
            }

            body * {
              visibility: hidden;
            }

            .print-area,
            .print-area * {
              visibility: visible;
            }

            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }

            .print-hide {
              display: none !important;
            }

            /* Make sure signature image is printed */
            .signature-image {
              display: block !important;
              visibility: visible !important;
            }
          }
        `}
      </style>

      <div className="bg-slate-100 min-h-screen p-4 print:bg-white print:p-0">
        {/* PRINT BUTTON */}
        <div className="flex justify-end mb-4 print-hide">
          <button
            onClick={handlePrint}
            className="bg-primary text-white px-5 py-3 rounded-xl"
          >
            Print
          </button>
        </div>

        {/* PAPER */}
        <div className="print-area w-full max-w-[1100px] mx-auto bg-white p-4 text-[12px] leading-tight min-h-[1120px] flex flex-col justify-between">
          {/* CONTENT */}
          <div className="flex-1">
            {/* HEADER */}
            <ReportHeader />

            {/* TOP DETAILS */}
            <div className="grid grid-cols-3 gap-4 mt-8 mb-4 text-[15px]">
              <div>
                <span className="font-bold">Date:</span>{" "}
                {formatDate(weightTest.date)}
              </div>

              <div>
                <span className="font-bold">Due Date:</span>{" "}
                {formatDate(weightTest.dueDate)}
              </div>

              <div>
                <span className="font-bold">Form No:</span>{" "}
                <span className="text-red-600 font-bold text-[12px]">
                  {weightTest.formNo}
                </span>
              </div>
            </div>

            {/* CUSTOMER */}
            <div className="mt-8 mb-5 text-[15px]">
              <div className="mb-3">
                <span className="font-bold">Customer:</span>{" "}
                {weightTest.customerName}
              </div>

              <div className="mb-5">
                <span className="font-bold">Address:</span>{" "}
                {weightTest.customerAddress}
              </div>
            </div>

            {/* TABLE */}
            <div className="mb-6 mt-8">
              <div className="font-bold text-[18px] mb-5">
                Weight Test Line Items
              </div>

              <table className="w-full border border-black border-collapse text-[12px]">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-black p-2">Capacity</th>

                    <th className="border border-black p-2">S/No.</th>

                    <th className="border border-black p-2">Actual Reading</th>
                  </tr>
                </thead>

                <tbody>
                  {weightTest.weightTestLineItems?.map(
                    (item: any, index: number) => (
                      <tr key={index}>
                        <td className="border border-black p-2 text-center">
                          {item.capacity}
                          {item.capacityUnit}
                        </td>

                        <td className="border border-black p-2 text-center">
                          {item.weightBox}
                        </td>

                        <td className="border border-black p-2 text-center">
                          {item.actualReading}
                          {item.actualReadingUnit}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= BOTTOM SECTION ================= */}
          <div className="mt-auto">
            {/* SIGNATURE SECTION */}
            <div className="grid grid-cols-3 gap-10 mb-2 text-[14px]">
              {/* SIGNATURE */}
              <div className="relative">
                <div className="font-bold">Signature</div>

                <div className="relative mt-2 h-[40px] border-b border-black">
                  <img
                    src="/signature.png"
                    alt="Signature"
                    className="
            signature-image
            absolute
            left-0
            bottom-[-18px]
            max-h-[75px]
            max-w-[240px]
            object-contain
            object-left-bottom
            z-10
          "
                  />
                </div>
              </div>

              {/* SIGNED BY */}
              <div>
                <div className="font-bold">Signed By</div>

                <div className="relative mt-2 h-[40px] border-b border-black flex items-end pb-1">
                  {weightTest.signedBy}
                </div>
              </div>

              {/* DESIGNATION */}
              <div>
                <div className="font-bold">Designation</div>

                <div className="relative mt-2 h-[40px] border-b border-black flex items-end pb-1">
                  {weightTest.designation}
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <ReportFooter message="Weights are tested on calibrated scale with NMI R76 regulation 13 certified test weights." />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewWeightTest;
